import React, { useEffect, useMemo, useState } from 'react'
import { Formik } from 'formik'
import { withCapture, _ } from 'utils'
import FullPageLoading from 'components/FullPageLoading'
import FormTabs from 'features/forms/FormTabs'
import { useEventSlug, useQuery, useSlug } from 'hooks'
import { getNodes } from 'graphql/utils'
import {
    GetMeetingSpaceQuery,
    GetMeetingSpaceQueryVariables,
    UpdateMeetingSpace
} from 'generated/graphql'
import { getChangedValues } from 'features/forms/utils'
import { useDispatch } from 'react-redux'
import { showNotification } from 'features/notifications'
import { getEventData } from 'features/events/eventSlice'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import Meetings from './tabs/Meetings'
import Settings from './tabs/Settings'
import { meetingSpaceQuery } from './graphql/query'
import { updateMeetingSpace } from './graphql/mutation'

const transformDataForForm = (data: UpdateMeetingSpace) => ({
    parent: {
        name: data.parent?.name,
        enableChat: data.parent?.enableChat,
        image: data.parent?.image,
        helpContent: data.parent?.helpContent,
        helpContentType: data.parent?.helpContentType,
        published: data.parent?.published,
    }
})

const MeetingSpace = () => {
    const dispatch = useDispatch()
    const [isUploading, setIsUploading] = useState(false)
    const eventSlug = useEventSlug()
    const meetingSlug = useSlug('meetingSlug')
    const [initialValues, setInitialValues] = useState({})

    const { data } = useQuery<
        GetMeetingSpaceQuery, GetMeetingSpaceQueryVariables
    >(meetingSpaceQuery, { eventId: eventSlug, spaceFilters: { parent: { slug: meetingSlug } } })
    const meetingSpaceData = getNodes(data?.event?.meetingSpaces)[0]
    useEffect(() => {
        if (meetingSpaceData) {
            setInitialValues(transformDataForForm(meetingSpaceData))
        }
    }, [meetingSpaceData])

    const handleSubmit = withCapture(async (values, { resetForm }) => {
        setIsUploading(true)
        const submitValues = getChangedValues(initialValues, values) as UpdateMeetingSpace
        const response = await updateMeetingSpace(meetingSpaceData.id, submitValues)
        setIsUploading(false)

        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            resetForm({ values: initialValues })
            return
        }

        const newValues = {
            parent: {
                ...values.parent,
                image: response.data?.updateMeetingSpace.parent.image
            }
        }
        resetForm({ values: newValues })
        setInitialValues(newValues)
        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.update_success'),
        }))

        // If the name/published fields are changed, re-fetch event data for Sidebar
        if (submitValues?.parent?.name || submitValues?.parent?.published !== undefined) {
            dispatch(getEventData(eventSlug))
        }
    })

    const MeetingsMemo = useMemo(() => (
        () => (
            meetingSpaceData?.id ? <Meetings spaceId={meetingSpaceData?.id} /> : <div />
        )
    ), [meetingSpaceData?.id])

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            <>
                <FormTabs
                    tabs={[
                        {
                            id: 'meetings',
                            name: _('meeting.tabs.meetings'),
                            Component: MeetingsMemo
                        },
                        {
                            id: 'settings',
                            name: _('meeting.tabs.settings'),
                            Component: Settings
                        },
                    ]}
                    showHelpContent
                    showSpaceOpened
                />
                {isUploading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    )
}

export default MeetingSpace
